import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Badge, Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import { customStyles } from './MultiSelectStyles';
import { ToastContainer, toast, Flip } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faSpinner, 
  faSync,
  faCheck,
  faExclamationTriangle,
  faTags,
  faFileAlt,
  faChartPie,
  faInfoCircle,
  faPlus,
  faChartBar,
  faTable,
  faTimes,
  faFileLines,
  faCheckCircle,
  faLightbulb,
  faEdit,
  faPaperPlane,
  faCalendarAlt,
  faBuilding,
  faShieldAlt,
  faListUl,
  faClipboardCheck,
  faListCheck,
  faAngleRight,
  faClipboardList,
  faTag,
  faPercent,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { documentsAPI, projectsAPI } from '../../services/api';
import './DocumentDetail.css';

// Add additional CSS for the redesigned page
const styles = {
  pageContainer: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    paddingBottom: '2rem'
  },
  headerCard: {
    borderLeft: '4px solid #0d6efd',
    borderRadius: '0.5rem',
    boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
  },
  sectionCard: {
    borderRadius: '0.5rem',
    boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
    marginBottom: '1.5rem',
    border: 'none'
  },
  sectionHeader: {
    borderBottom: '1px solid #e9ecef',
    padding: '1rem',
    backgroundColor: '#f8f9fa'
  },
  tagBadge: {
    padding: '0.5rem',
    margin: '0.25rem',
    fontSize: '0.85rem'
  },
  frameworkBadge: {
    padding: '0.5rem',
    margin: '0.25rem',
    fontSize: '0.85rem',
    backgroundColor: '#17a2b8'
  },
  tabContent: {
    padding: '1.5rem'
  },
  actionButton: {
    borderRadius: '0.375rem',
    boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
  },
  metadataLabel: {
    fontWeight: 'bold',
    color: '#6c757d'
  },
  metadataValue: {
    color: '#212529'
  },
  analysisSection: {
    backgroundColor: '#f8f9fa',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '1rem'
  },
  chatContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '0.5rem'
  }
};

const DocumentDetail = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const chatMessagesRef = React.useRef(null);
  const typingTimerRef = React.useRef(null);
  
  const [document, setDocument] = useState(null);
  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [tags, setTags] = useState([]);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showEditTagsModal, setShowEditTagsModal] = useState(false);
  const [editableTags, setEditableTags] = useState([]);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [customTag, setCustomTag] = useState('');
  const [savingTags, setSavingTags] = useState(false);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [editableFrameworks, setEditableFrameworks] = useState([]);
  const [documentQuery, setDocumentQuery] = useState('');
  const [queryResponse, setQueryResponse] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(0); // Track which accordion item is expanded
  const [isQuerying, setIsQuerying] = useState(false);
  const [showQuerySlider, setShowQuerySlider] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const [currentUserQuery, setCurrentUserQuery] = useState('');
  const [displayedResponse, setDisplayedResponse] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Predefined policy tags
  const policyTags = [
    'Privacy Policy',
    'Terms of Service',
    'Security Policy',
    'Acceptable Use Policy',
    'Cookie Policy',
    'GDPR Compliance',
    'HIPAA Compliance',
    'Data Retention Policy',
    'Incident Response Policy',
    'Business Continuity Plan',
    'Employee Handbook',
    'Code of Conduct',
    'Environmental Policy',
    'Health and Safety Policy',
    'Social Media Policy',
    'Remote Work Policy',
    'Anti-Discrimination Policy',
    'Whistleblower Policy',
    'Conflict of Interest Policy',
    'Information Security Policy'
  ];
  
  // Compliance frameworks list
  const frameworks = [
    'CIS Critical Security Controls',
    'COBIT (Control Objectives for IT)',
    'Companies Act 2013',
    'CSA Cloud Controls Matrix (CCM)',
    'EU AI Act',
    'GDPR (EU General Data Protection Regulation)',
    'HIPAA / HITECH',
    'HITRUST CSF',
    'IEC 27032',
    'India DPDP Act 2023',
    'IRDAI Risk & Governance Guidelines',
    'ISO 13485',
    'ISO 19600',
    'ISO 37301',
    'ISO/IEC 20000',
    'ISO/IEC 22301',
    'ISO/IEC 27001',
    'ISO/IEC 27017',
    'ISO/IEC 27018',
    'ISO/IEC 27701',
    'ISO/IEC 31000',
    'NIST Cybersecurity Framework (CSF)',
    'NIST Privacy Framework',
    'Payment Card Industry DSS (PCI DSS)',
    'RBI Cybersecurity & BCP Master Directions',
    'RBI Prudential Regulations (PCA, Basel)',
    'Sarbanes-Oxley Act (SOX)',
    'SEBI LODR (Listing Obligations & Disclosure Requirements)',
    'SOC 1 and SOC 2 (Trust Services Criteria)',
    'TISAX (Trusted Information Security Assessment Exchange)'
  ];
  
  // API URL is handled by the API service

  useEffect(() => {
    fetchDocumentDetails();
  }, [documentId]);

  const fetchDocumentDetails = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch document details
      const documentResponse = await documentsAPI.getDocument(documentId);
      
      if (documentResponse.data.status === 200 && documentResponse.data.data.document) {
        const documentData = documentResponse.data.data.document;
        setDocument(documentData);
        
        // Fetch project details if document has project_id
        if (documentData.project_id) {
          const projectResponse = await projectsAPI.getProject(documentData.project_id);
          
          if (projectResponse.data.status === 200 && projectResponse.data.data.project) {
            setProject(projectResponse.data.data.project);
          }
        }
        
        // Fetch document tags and frameworks
        try {
          const tagsResponse = await documentsAPI.getDocumentTags(documentId);
          if (tagsResponse.data.status === 200) {
            // Set tags if available
            if (tagsResponse.data.data.tags) {
              setTags(tagsResponse.data.data.tags);
              setSelectedTags(tagsResponse.data.data.tags);
            }
            
            // Set frameworks if available
            if (tagsResponse.data.data.frameworks) {
              setSelectedFrameworks(tagsResponse.data.data.frameworks);
            }
          }
        } catch (tagsErr) {
          console.error('Error fetching document tags and frameworks:', tagsErr);
          // Don't set error for tags, just log it
        }
        
        // Fetch analysis if document has been analyzed
        if (documentData.analyzed) {
          const analysisResponse = await documentsAPI.getDocumentAnalysis(documentId);
          
          if (analysisResponse.data.status === 200) {
            setAnalysis(analysisResponse.data.data.analysis);
          }
        }
      } else {
        setError('Document not found');
      }
    } catch (err) {
      console.error('Error fetching document details:', err);
      setError('Failed to load document details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeDocument = async () => {
    setAnalyzing(true);
    setError('');
    
    // Show initial toast notification with enhanced styling
    const toastId = toast.info(
      <div className="d-flex align-items-center">
        <div className="spinner-border spinner-border-sm text-info me-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div>
          <strong className="text-primary">Step 1/4:</strong> Fetching data from document...
        </div>
      </div>, 
      {
        autoClose: false,
        position: 'top-right',
        className: 'toast-with-progress',
        progressClassName: 'progress-bar-animated progress-bar-striped bg-info',
        icon: '🔍'
      }
    );
    
    try {
      // Update toast after a delay to simulate progress
      setTimeout(() => {
        toast.update(toastId, {
          render: (
            <div className="d-flex align-items-center">
              <div className="spinner-grow spinner-grow-sm text-warning me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>
                <strong className="text-warning">Step 2/4:</strong> Analyzing document content...
              </div>
            </div>
          ),
          autoClose: false,
          className: 'toast-with-progress',
          progressClassName: 'progress-bar-animated progress-bar-striped bg-warning',
          icon: '📄'
        });
      }, 1500);
      
      // Make the API call to analyze the document
      const response = await documentsAPI.requestAnalysis(documentId);
      
      // Update toast again to show more progress
      setTimeout(() => {
        toast.update(toastId, {
          render: (
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm text-primary me-2" 
                   style={{borderWidth: '3px'}} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>
                <strong className="text-primary">Step 3/4:</strong> Generating AI insights...
              </div>
            </div>
          ),
          autoClose: false,
          className: 'toast-with-progress',
          progressClassName: 'progress-bar-animated progress-bar-striped bg-primary',
          icon: '🤖'
        });
      }, 3000);
      
      if (response.data && response.data.status === 200) {
        // Final delay before completing
        setTimeout(() => {
          toast.update(toastId, {
            render: (
              <div className="d-flex align-items-center">
                <div className="me-2">
                  <i className="fas fa-check-circle text-success" style={{fontSize: '1.2rem'}}></i>
                </div>
                <div>
                  <strong className="text-success">Step 4/4:</strong> Analysis complete!
                  <div className="small">Document insights are ready to view</div>
                </div>
              </div>
            ),
            type: 'success',
            autoClose: 5000,
            className: 'toast-success-custom',
            progressClassName: 'bg-success',
            icon: '✅'
          });
          
          // Refresh document data after analysis
          fetchDocumentDetails();
        }, 4500);
      } else {
        toast.update(toastId, {
          render: (
            <div className="d-flex align-items-center">
              <div className="me-2">
                <i className="fas fa-exclamation-triangle text-danger" style={{fontSize: '1.2rem'}}></i>
              </div>
              <div>
                <strong className="text-danger">Analysis Failed!</strong>
                <div>{response.data?.message || 'Failed to analyze document'}</div>
              </div>
            </div>
          ),
          type: 'error',
          autoClose: 5000,
          className: 'toast-error-custom',
          progressClassName: 'bg-danger',
          icon: '⚠️'
        });
        setError(response.data?.message || 'Failed to analyze document');
      }
    } catch (err) {
      console.error('Error analyzing document:', err);
      
      toast.update(toastId, {
        render: (
          <div className="d-flex align-items-center">
            <div className="me-2">
              <i className="fas fa-times-circle text-danger" style={{fontSize: '1.2rem'}}></i>
            </div>
            <div>
              <strong className="text-danger">Error Occurred!</strong>
              <div>{err.response?.data?.message || 'Failed to analyze document'}</div>
            </div>
          </div>
        ),
        type: 'error',
        autoClose: 5000,
        className: 'toast-error-custom',
        progressClassName: 'bg-danger',
        icon: '❌'
      });
      
      if (err.response) {
        setError(err.response.data.message || 'Failed to analyze document');
      } else {
        setError('Network error. Please try again later.');
      }
    } finally {
      setTimeout(() => setAnalyzing(false), 4500);
    }
  };
  
  const handleOpenEditTagsModal = () => {
    // Initialize with current tags from analysis or empty array
    const currentTags = analysis?.analysis_json?.tag_matches || [];
    setEditableTags(currentTags.map(tag => ({
      ...tag,
      isEdited: false
    })));
    
    // Initialize with current selected frameworks
    setEditableFrameworks(selectedFrameworks.map(framework => ({
      framework,
      isEdited: false
    })));
    
    setShowEditTagsModal(true);
  };
  
  const handleEditTag = (index, field, value) => {
    const updatedTags = [...editableTags];
    updatedTags[index] = {
      ...updatedTags[index],
      [field]: value,
      isEdited: true
    };
    setEditableTags(updatedTags);
  };
  
  const handleAddTag = () => {
    // Don't add empty tags or if the tag already exists
    if (!customTag.trim() || editableTags.some(t => t.tag === customTag.trim())) {
      return;
    }
    
    // Add a new tag to the editable tags list
    const newTag = {
      tag: customTag.trim(),
      confidence: 50, // Default confidence
      isEdited: true
    };
    
    setEditableTags([...editableTags, newTag]);
    setCustomTag(''); // Clear the input field
  };
  

  
  // Fetch chat history for the document
  const fetchChatHistory = async () => {
    setLoadingChatHistory(true);
    try {
      const response = await documentsAPI.getChatHistory(documentId);
      
      if (response.data && response.data.status === 200) {
        setChatHistory(response.data.data);
      } else {
        console.error('Failed to fetch chat history:', response.data?.message);
      }
    } catch (err) {
      console.error('Error fetching chat history:', err);
    } finally {
      setLoadingChatHistory(false);
    }
  };
  
  const toggleQuerySlider = () => {
    const newState = !showQuerySlider;
    setShowQuerySlider(newState);
    
    // When opening the slider
    if (newState) {
      setDocumentQuery('');
      setQueryResponse(null);
      // Fetch chat history when opening the slider
      fetchChatHistory();
    }
  };

  // Function to scroll chat to bottom
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Effect to scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, queryResponse, displayedResponse]);
  
  /**
   * Simulates a typing effect for displaying text character by character
   * @param {any} text - The text to display with typing effect
   */
  const simulateTypingEffect = (text) => {
    // Convert input to string and handle null/undefined
    const textString = String(text || '');
    
    // Exit early if no text to display
    if (!textString) {
      console.log('No text provided for typing effect');
      setIsTyping(false);
      return;
    }
    
    console.log('Starting typing effect with text length:', textString.length);
    
    // Clean up any existing timer
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
    }
    
    // Initialize state
    setIsTyping(true);
    setDisplayedResponse('');
    
    // Set up typing parameters
    let i = 0;
    const speed = 15; // milliseconds between characters
    
    try {
      // Set up interval to add one character at a time
      typingTimerRef.current = setInterval(() => {
        if (i < textString.length) {
          // Add next character
          setDisplayedResponse(prev => prev + textString.charAt(i));
          i++;
          scrollToBottom();
        } else {
          // Typing complete
          console.log('Typing effect completed');
          clearInterval(typingTimerRef.current);
          setIsTyping(false);
        }
      }, speed);
    } catch (error) {
      // Handle any errors by showing full text immediately
      console.error('Error in typing effect:', error);
      setDisplayedResponse(textString);
      setIsTyping(false);
    }
  };
  
  // Clean up typing effect on unmount
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
      }
    };
  }, []);

  const handleDocumentQuery = async (e) => {
    e.preventDefault();
    if (!documentQuery.trim()) {
      toast.warning('Please enter a query');
      return;
    }
    
    const queryText = documentQuery.trim();
    
    // Set current user query for immediate display
    setCurrentUserQuery(queryText);
    
    // Clear the text box immediately after sending
    setDocumentQuery('');
    
    // Start querying process
    setIsQuerying(true);
    setError('');
    setDisplayedResponse('');
    
    // Safety timeout to ensure isQuerying is reset after 30 seconds
    const safetyTimeout = setTimeout(() => {
      if (isQuerying) {
        console.log('Safety timeout triggered - resetting query state');
        setIsQuerying(false);
        setError('The request is taking longer than expected. Please try again.');
      }
    }, 30000);
    
    try {
      console.log('Sending query to API:', queryText);
      const response = await documentsAPI.queryDocumentWithAI(documentId, queryText);
      console.log('API response received:', response);
      
      // Clear safety timeout since we got a response
      clearTimeout(safetyTimeout);
      
      // Always reset querying state
      setIsQuerying(false);
      
      if (response.data && response.data.status === 200) {
        // Add the query text to the response object for display
        const responseWithQuery = {
          ...response.data.data,
          query: queryText
        };
        
        console.log('Response with query:', responseWithQuery);
        
        // Set the response
        setQueryResponse(responseWithQuery);
        
        // Try to use typing effect for the answer
        try {
          if (responseWithQuery.answer) {
            console.log('Starting typing effect for answer');
            simulateTypingEffect(responseWithQuery.answer);
          } else {
            console.log('No answer in response');
            setIsTyping(false);
          }
        } catch (typingError) {
          console.error('Error in typing effect:', typingError);
          // If typing effect fails, just show the answer directly
          setDisplayedResponse(responseWithQuery.answer || '');
          setIsTyping(false);
        }
        
        // Refresh chat history after successful query
        fetchChatHistory();
        
        // Scroll to bottom
        setTimeout(scrollToBottom, 100);
      } else {
        console.error('API returned error status:', response.data);
        setError(response.data?.message || 'Failed to process query');
      }
    } catch (err) {
      // Clear safety timeout since we got an error
      clearTimeout(safetyTimeout);
      
      console.error('Error querying document:', err);
      setError('An error occurred while processing your query. Please try again.');
      setIsQuerying(false);
    }
  };
  
  const handleSaveTagEdits = async () => {
    setIsReanalyzing(true);
    try {
      // Extract just the tag names from the editable tags for the API call
      const tagNames = editableTags.map(tag => tag.tag);
      
      // Extract just the framework names from the editable frameworks for the API call
      // Make sure we're extracting the correct property based on the structure
      console.log('Raw editableFrameworks:', editableFrameworks);
      const frameworkNames = editableFrameworks.map(fw => {
        // Handle both possible structures: {framework: name} or {value: name}
        const frameworkName = fw.framework || fw.value;
        console.log('Extracted framework name:', frameworkName, 'from:', fw);
        return frameworkName;
      });
      console.log('Final frameworkNames array:', frameworkNames);
      
      // Call the API to update tags and frameworks
      const response = await documentsAPI.updateDocumentTagsAndFrameworks(documentId, tagNames, frameworkNames);
      console.log('API response:', response.data);
      
      // Check for successful status code (200)
      if (response.data.status === 200 || response.data.status === 'success') {
        // Update the document state with the new tags and frameworks
        setDocument(prev => ({
          ...prev,
          tags: tagNames,
          frameworks: frameworkNames
        }));
        
        // Also update the selectedFrameworks state which is used for display
        setSelectedFrameworks(frameworkNames);
        
        // If analysis exists, update the tag_matches in the analysis_json
        if (analysis && analysis.analysis_json) {
          setAnalysis(prev => ({
            ...prev,
            analysis_json: {
              ...prev.analysis_json,
              tag_matches: editableTags
            }
          }));
        }
        
        toast.success('Tags and frameworks updated successfully!');
      } else {
        toast.error('Failed to update tags and frameworks.');
      }
      
      setShowEditTagsModal(false);
      
      // Clear any previous errors
      setError('');
    } catch (err) {
      setError('Failed to update tags and frameworks');
      toast.error('Failed to update tags and frameworks');
      console.error(err);
    } finally {
      setIsReanalyzing(false);
      setCustomTag(''); // Clear the custom tag input when closing modal
    }
  };
  
  const handleReanalyzeDocument = async () => {
    setIsReanalyzing(true);
    setError('');
    
    // Show initial toast notification
    const toastId = toast.info('Fetching updated document data...', {
      autoClose: false,
      position: 'top-right'
    });
    
    try {
      // Store current tags before re-analysis
      const currentTags = analysis?.analysis_json?.tag_matches ? [...analysis.analysis_json.tag_matches] : [];
      
      // Update toast after a delay to simulate progress
      setTimeout(() => {
        toast.update(toastId, {
          render: 'Re-analyzing document content...',
          autoClose: false
        });
      }, 1500);
      
      // Make the API call to re-analyze the document
      const response = await documentsAPI.requestAnalysis(documentId);
      
      // Update toast again to show more progress
      setTimeout(() => {
        toast.update(toastId, {
          render: 'Generating updated AI insights...',
          autoClose: false
        });
      }, 3000);
      
      if (response.data && response.data.status === 200) {
        // Fetch updated document details but preserve our tags
        const documentResponse = await documentsAPI.getDocument(documentId);
        
        if (documentResponse.data.status === 200 && documentResponse.data.data.document) {
          setDocument(documentResponse.data.data.document);
          
          // Fetch new analysis
          const analysisResponse = await documentsAPI.getDocumentAnalysis(documentId);
          
          if (analysisResponse.data.status === 200) {
            // Preserve our edited tags in the new analysis
            const newAnalysis = analysisResponse.data.data.analysis;
            
            if (newAnalysis && newAnalysis.analysis_json) {
              // Replace the tags in the new analysis with our edited tags
              newAnalysis.analysis_json.tag_matches = currentTags;
              setAnalysis(newAnalysis);
              
              // Final success toast
              setTimeout(() => {
                toast.update(toastId, {
                  render: 'Re-analysis complete!',
                  type: toast.TYPE.SUCCESS,
                  autoClose: 3000
                });
              }, 1000);
            }
          }
        }
      } else {
        toast.update(toastId, {
          render: response.data?.message || 'Failed to re-analyze document',
          type: toast.TYPE.ERROR,
          autoClose: 3000
        });
        setError(response.data?.message || 'Failed to re-analyze document');
      }
    } catch (err) {
      toast.update(toastId, {
        render: 'An error occurred while reanalyzing the document',
        type: toast.TYPE.ERROR,
        autoClose: 3000
      });
      setError('An error occurred while reanalyzing the document');
      console.error(err);
    } finally {
      setTimeout(() => setIsReanalyzing(false), 4500);
    }
  };

  const handleDeleteDocument = async () => {
    if (window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      try {
        const response = await documentsAPI.deleteDocument(documentId);
        
        if (response.data.status === 200) {
          navigate('/documents');
        } else {
          setError(response.data.message || 'Failed to delete document');
        }
      } catch (err) {
        console.error('Error deleting document:', err);
        setError('Failed to delete document. Please try again later.');
      }
    }
  };

  const handleOpenTagsModal = () => {
    setSelectedTags([...tags]);
    setShowTagsModal(true);
  };

  const handleCloseTagsModal = () => {
    setShowTagsModal(false);
    setCustomTag('');
  };

  const handleTagToggle = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleAddCustomTag = () => {
    if (customTag && !selectedTags.includes(customTag)) {
      setSelectedTags([...selectedTags, customTag]);
      setCustomTag('');
    }
  };

  const handleRemoveTag = (index) => {
    // Remove a tag from the editable tags list
    const updatedTags = [...editableTags];
    updatedTags.splice(index, 1);
    setEditableTags(updatedTags);
    
    // This ensures the react-select component updates correctly
    // by forcing a re-render with the new state
  };
  
  // Function to handle removing a tag by its name (for use with react-select)
  const handleRemoveTagByName = (tagName) => {
    setEditableTags(editableTags.filter(tag => tag.tag !== tagName));
  };
  
  // Function to handle accordion toggle
  const handleAccordionToggle = (idx) => {
    setExpandedAccordion(expandedAccordion === idx ? -1 : idx);
  };

  // const handleSaveTags = async () => {
  //   setSavingTags(true);
  //   try {
  //     const response = await documentsAPI.updateDocumentTags(documentId, selectedTags);
      
  //     if (response.data.status === 200) {
  //       setTags(selectedTags);
  //       setShowTagsModal(false);
  //     } else {
  //       alert(response.data.message || 'Failed to save tags');
  //     }
  //   } catch (err) {
  //     console.error('Error saving tags:', err);
  //     alert('Failed to save tags. Please try again.');
  //   } finally {
  //     setSavingTags(false);
  //   }
  // };

  // const handleDownloadDocument = () => {
  //   if (document) {
  //     // Use the documentsAPI for downloading
  //     documentsAPI.downloadDocument(documentId)
  //       .then(response => {
  //         // Create a blob from the response data
  //         const blob = new Blob([response.data]);
  //         const url = window.URL.createObjectURL(blob);
  //         const a = document.createElement('a');
  //         a.href = url;
  //         a.download = document.file_name || `document-${documentId}`;
  //         document.body.appendChild(a);
  //         a.click();
  //         window.URL.revokeObjectURL(url);
  //       })
  //       .catch(err => {
  //         console.error('Error downloading document:', err);
  //         setError('Failed to download document');
  //       });
  //   }
  // };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
        <h3>Loading document details...</h3>
      </Container>
    );
  }

  if (!document) {
    return (
      <Container className="text-center py-5">
        <Alert variant="danger">
          Document not found or you don't have permission to access it.
        </Alert>
        <Button as={Link} to="/documents" variant="primary" className="mt-3">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Documents
        </Button>
      </Container>
    );
  }

  return (
    <>
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Flip}
      />
      <div style={styles.pageContainer}>
        <Container className="py-4">
          {/* Back button */}
          <div className="mb-4">
            <Button 
              variant="outline-secondary" 
              as={Link} 
              to={project ? `/projects/${project.id}` : '/documents'}
              className="mb-3"
              style={styles.actionButton}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to {project ? 'Project' : 'Documents'}
            </Button>
          </div>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {error}
            </Alert>
          )}
          
          {/* Document Header Card */}
          <Card className="mb-4" style={styles.headerCard}>
            <Card.Body>
              <Row className="align-items-center">
                <Col md={8}>
                  <h2 className="mb-2">
                    <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
                    {document.title}
                  </h2>
                  <div className="d-flex align-items-center text-muted mb-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    <span>Uploaded on {new Date(document.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    <span className="mx-2">•</span>
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    <span>{document.file_type.toUpperCase()}</span>
                    {project && (
                      <>
                        <span className="mx-2">•</span>
                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                        <span>{project.name}</span>
                      </>
                    )}
                  </div>
                  <div>
                    {document.analyzed ? (
                      <Badge bg="success" className="me-2">Analyzed</Badge>
                    ) : (
                      <Badge bg="warning" className="me-2">Not Analyzed</Badge>
                    )}
                    {isReanalyzing && (
                      <Badge bg="info" className="me-2">Reanalyzing</Badge>
                    )}
                  </div>
                </Col>
                <Col md={4} className="text-md-end mt-3 mt-md-0">
                  <div className="d-flex flex-wrap justify-content-md-end gap-2">
                    {document.analyzed && (
                      <>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={toggleQuerySlider}
                          style={styles.actionButton}
                        >
                          <FontAwesomeIcon icon={faLightbulb} className="me-1" /> Chat with Document
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={handleOpenEditTagsModal}
                          style={styles.actionButton}
                        >
                          <FontAwesomeIcon icon={faTags} className="me-1" /> Edit Tags
                        </Button>
                        <Button 
                          variant="outline-success" 
                          size="sm"
                          onClick={handleAnalyzeDocument}
                          disabled={analyzing}
                          style={styles.actionButton}
                        >
                          {analyzing ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                              Re-analyzing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSync} className="me-1" />
                              Re-analyze
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          {/* Top Policy Matches */}
          {document.analyzed && analysis && analysis.analysis_json && analysis.analysis_json.tag_matches && analysis.analysis_json.tag_matches.length > 0 && (
            <div className="top-policy-matches mb-4 p-4 bg-white rounded shadow-sm">
              <h4 className="mb-3">
                <FontAwesomeIcon icon={faTags} className="me-2 text-primary" />
                Top Policy Matches
              </h4>
              <Row>
                {analysis.analysis_json.tag_matches
                  .sort((a, b) => b.confidence - a.confidence)
                  .slice(0, 3)
                  .map((tag, index) => (
                    <Col md={4} key={index}>
                      <div className="policy-match-card p-3 bg-light rounded h-100">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5 className="mb-0">{tag.tag}</h5>
                          <Badge 
                            bg={tag.confidence > 70 ? 'success' : 
                                tag.confidence > 40 ? 'warning' : 'danger'}
                            className="px-2 py-1"
                          >
                            {tag.confidence}%
                          </Badge>
                        </div>
                        <p className="tag-explanation mb-0">{tag.explanation}</p>
                      </div>
                    </Col>
                  ))
                }
              </Row>
            </div>
          )}
            
            <Row>
              {/* Left Column - Document Preview */}
     
              
              {/* Right Column - Analysis Results */}
              <Col lg={12} id="analysis-section">
                {document.analyzed ? (
                  analysis ? (
                    <div className="analysis-results">
                      {analysis.analysis_json ? (
                        <>
                          <Row>
                            {/* Left Column - Overall Assessment + Document Structure */}
                            <Col md={6}>
                              {/* Overall Assessment Card */}
                              <div className="analysis-card bg-white rounded shadow-sm mb-4">
                                <div className="section-header p-3 border-bottom">
                                  <h4 className="mb-0">
                                    <FontAwesomeIcon icon={faChartBar} className="me-2 text-primary" />
                                    Version Information
                                  </h4>
                                </div>
                                <div className="p-3">
                                  <div className="version-info p-3 rounded">
                                    {analysis.analysis_json.version_info && (
                                      <dl className="row mb-0">

                                            <dt className="col-sm-6 border-bottom">Version</dt>
                                            <dt className="col-sm-6 border-bottom">{analysis.analysis_json.version_info.has_version || 'N/A' }</dt>
                                            <dt className="col-sm-6 border-bottom">Version Number</dt>
                                            <dt className="col-sm-6 border-bottom">{analysis.analysis_json.version_info.version_number || 'N/A'}</dt>
                                        
                                            <dt className="col-sm-6 border-bottom">Last Review</dt>
                                            <dt className="col-sm-6 border-bottom">{analysis.analysis_json.version_info.last_review_date || 'N/A'}</dt>
                                     
                                       
                                            <dt className="col-sm-6 border-bottom">Next Review</dt>
                                            <dt className="col-sm-6 border-bottom">{analysis.analysis_json.version_info.next_review_date || 'N/A'}</dt>
                                      
                                      </dl>
                                    )}
                                  </div>
                                </div>
                              </div>
                               
                              {/* Document Structure Card */}
                              <div className="analysis-card bg-white rounded shadow-sm mb-4">
                                <div className="section-header p-3 border-bottom">
                                  <h4 className="mb-0">
                                    <FontAwesomeIcon icon={faTable} className="me-2 text-primary" />
                                    Document Structure
                                  </h4>
                                </div>
                                <div className="p-3">
                                  {/* Document Components */}
                                  <div className="analysis-detail-section mb-4">
                                    <h5 className="border-bottom pb-2">Required Components</h5>
                                    <div className="component-grid mt-3">
                                      {Object.entries(analysis.analysis_json.document_components).map(([key, value]) => {
                                        const formattedKey = key
                                          .replace('has_', '')
                                          .split('_')
                                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                          .join(' ');
                                        
                                        return (
                                          <div key={key} className="component-item d-flex align-items-center p-2 border-bottom">
                                            <span className="component-name flex-grow-1">{formattedKey}</span>
                                            <Badge 
                                              bg={value ? 'success' : 'danger'}
                                              className="component-status"
                                            >
                                              {value ? 'Present' : 'Missing'}
                                            </Badge>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  
                                  {/* Version Information */}
                                </div>
                              </div>
                            </Col>
                            
                            {/* Right Column - Content Analysis + Policy Tag Matches */}
                            <Col md={6}>
                              {/* Document Content Analysis Card - Modern Design */}
                              <div className="analysis-card bg-white rounded shadow-sm mb-4">
                                <div className="section-header p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
                                  <h4 className="mb-0 d-flex align-items-center">
                                    <div className="icon-circle bg-primary text-white me-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%' }}>
                                      <FontAwesomeIcon icon={faFileAlt} />
                                    </div>
                                    Content Analysis
                                  </h4>
                                  {/* <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    onClick={handleReanalyzeDocument}
                                    disabled={isReanalyzing}
                                    className="d-flex align-items-center"
                                  >
                                    <FontAwesomeIcon icon={faSync} className={`me-1 ${isReanalyzing ? 'fa-spin' : ''}`} />
                                    {isReanalyzing ? 'Analyzing...' : 'Refresh Analysis'}
                                  </Button> */}
                                </div>
                                <div className="p-4">
                                  {/* Document Summary with modern card design */}
                                  <div className="analysis-detail-section mb-4 rounded shadow-sm border">
                                    <div className="d-flex align-items-center p-3 bg-white border-bottom">
                                      <div className="icon-circle bg-primary text-white me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '50%' }}>
                                        <FontAwesomeIcon icon={faFileLines} size="lg" />
                                      </div>
                                      <h5 className="mb-0 fw-bold">Document Summary</h5>
                                    </div>
                                    <div className="p-4 bg-white">
                                      {typeof analysis.analysis_json.summary === 'string' ? (
                                        <div className="summary-content">
                                          <p className="mb-0 lh-lg">{analysis.analysis_json.summary}</p>
                                        </div>
                                      ) : analysis.analysis_json.summary && typeof analysis.analysis_json.summary === 'object' ? (
                                        <div className="summary-content">
                                          <p className="mb-0 lh-lg">{JSON.stringify(analysis.analysis_json.summary)}</p>
                                        </div>
                                      ) : (
                                        <div className="summary-content text-muted">
                                          <p className="mb-0">No summary available</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Framework Alignment with modern card design */}
                                  <div className="analysis-detail-section mb-4 rounded shadow-sm border">
                                    <div className="d-flex align-items-center p-3 bg-white border-bottom">
                                      <div className="icon-circle bg-success text-white me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '50%' }}>
                                        <FontAwesomeIcon icon={faCheckCircle} size="lg" />
                                      </div>
                                      <h5 className="mb-0 fw-bold">Framework Alignment</h5>
                                      <Button 
                                        variant="outline-success" 
                                        size="sm" 
                                        className="ms-auto d-flex align-items-center" 
                                        onClick={handleOpenEditTagsModal}
                                      >
                                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                                        Edit Frameworks
                                      </Button>
                                    </div>
                                    <div className="p-3 bg-white">
                                      {/* Selected Frameworks Display */}
                                      {selectedFrameworks && selectedFrameworks.length > 0 ? (
                                        <div className="mb-4">
                                          <h6 className="fw-bold mb-3 d-flex align-items-center">
                                            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-success" />
                                            Selected Compliance Frameworks
                                          </h6>
                                          <div className="d-flex flex-wrap gap-2">
                                            {selectedFrameworks.map((framework, idx) => (
                                              <Badge 
                                                key={idx} 
                                                bg="success" 
                                                className="p-2 mb-2 d-flex align-items-center" 
                                                style={{ fontSize: '0.9rem', fontWeight: 'normal' }}
                                              >
                                                <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                                                {framework.framework || framework.value}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="alert alert-light border">
                                          <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-muted" />
                                          <span className="text-muted">No compliance frameworks selected</span>
                                        </div>
                                      )}
                                      
                                      {/* Display framework analysis with modern styling */}
                                      {typeof analysis.analysis_json.framework_analysis === 'string' ? (
                                        <div className="framework-analysis mt-3 p-3 border rounded">
                                          <h6 className="fw-bold mb-2 d-flex align-items-center">
                                            <FontAwesomeIcon icon={faChartBar} className="me-2 text-success" />
                                            Framework Analysis
                                          </h6>
                                          <p className="mb-0 lh-lg">{analysis.analysis_json.framework_analysis}</p>
                                        </div>
                                      ) : analysis.analysis_json.framework_analysis && typeof analysis.analysis_json.framework_analysis === 'object' ? (
                                        <div className="framework-analysis mt-4">
                                          {analysis.analysis_json.framework_analysis.summary && (
                                            <div className="analysis-summary mb-4 p-3 border rounded">
                                              <h6 className="fw-bold mb-3 d-flex align-items-center">
                                                <FontAwesomeIcon icon={faChartBar} className="me-2 text-success" />
                                                Analysis Summary
                                              </h6>
                                              <p className="mb-0 lh-lg">{analysis.analysis_json.framework_analysis.summary}</p>
                                            </div>
                                          )}
                                          {analysis.analysis_json.framework_analysis.key_frameworks && Array.isArray(analysis.analysis_json.framework_analysis.key_frameworks) && (
                                            <div className="key-frameworks mb-4 p-3 border rounded">
                                              <h6 className="fw-bold mb-3 d-flex align-items-center">
                                                <FontAwesomeIcon icon={faListUl} className="me-2 text-primary" />
                                                Key Frameworks
                                              </h6>
                                              <div className="d-flex flex-wrap gap-2">
                                                {analysis.analysis_json.framework_analysis.key_frameworks.map((framework, idx) => (
                                                  <Badge key={idx} bg="primary" className="p-2 mb-1">{framework}</Badge>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {analysis.analysis_json.framework_analysis.compliance_details && Array.isArray(analysis.analysis_json.framework_analysis.compliance_details) && (
                                            <div className="compliance-details mt-4">
                                              <h6 className="fw-bold mb-3 d-flex align-items-center">
                                                <FontAwesomeIcon icon={faClipboardCheck} className="me-2 text-info" />
                                                Compliance Details
                                              </h6>
                                              <div className="accordion" id="complianceAccordion">
                                                {analysis.analysis_json.framework_analysis.compliance_details.map((detail, idx) => (
                                                  <div key={idx} className="accordion-item mb-3 border rounded shadow-sm">
                                                    <h2 className="accordion-header" id={`heading${idx}`}>
                                                      <button 
                                                        className={`accordion-button ${expandedAccordion === idx ? '' : 'collapsed'}`}
                                                        type="button" 
                                                        onClick={() => handleAccordionToggle(idx)}
                                                        aria-expanded={expandedAccordion === idx ? "true" : "false"} 
                                                        aria-controls={`collapse${idx}`}
                                                      >
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                                          <span className="fw-bold">{detail.framework}</span>
                                                          <Badge 
                                                            bg={detail.compliance_level === 'high' ? 'success' : detail.compliance_level === 'medium' ? 'warning' : 'danger'}
                                                            className="ms-2 py-2 px-3"
                                                          >
                                                            {detail.compliance_level.toUpperCase()} Compliance
                                                          </Badge>
                                                        </div>
                                                      </button>
                                                    </h2>
                                                    <div 
                                                      id={`collapse${idx}`} 
                                                      className={`accordion-collapse collapse ${expandedAccordion === idx ? 'show' : ''}`} 
                                                      aria-labelledby={`heading${idx}`}
                                                    >
                                                      <div className="accordion-body p-4">
                                                        {detail.key_requirements && Array.isArray(detail.key_requirements) && (
                                                          <div className="mb-4">
                                                            <h6 className="fw-bold mb-2 d-flex align-items-center">
                                                              <FontAwesomeIcon icon={faListCheck} className="me-2 text-primary" />
                                                              Key Requirements
                                                            </h6>
                                                            <ul className="list-group list-group-flush">
                                                              {detail.key_requirements.map((req, i) => (
                                                                <li key={i} className="list-group-item bg-light py-2 d-flex align-items-center">
                                                                  <FontAwesomeIcon icon={faAngleRight} className="me-2 text-secondary" />
                                                                  {req}
                                                                </li>
                                                              ))}
                                                            </ul>
                                                          </div>
                                                        )}
                                                        {detail.gaps && (
                                                          <div className="mb-4">
                                                            <h6 className="fw-bold mb-2 d-flex align-items-center">
                                                              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                                                              Gaps
                                                            </h6>
                                                            <div className="p-3 bg-light rounded">{detail.gaps}</div>
                                                          </div>
                                                        )}
                                                        {detail.recommendations && (
                                                          <div>
                                                            <h6 className="fw-bold mb-2 d-flex align-items-center">
                                                              <FontAwesomeIcon icon={faLightbulb} className="me-2 text-success" />
                                                              Recommendations
                                                            </h6>
                                                            <div className="p-3 bg-light rounded">{detail.recommendations}</div>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <p className="mb-0">No framework alignment information available</p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Improvement Suggestions with modern card design */}
                                  <div className="analysis-detail-section mb-4 rounded shadow-sm border">
                                    <div className="d-flex align-items-center p-3 bg-white border-bottom">
                                      <div className="icon-circle bg-warning text-white me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '50%' }}>
                                        <FontAwesomeIcon icon={faLightbulb} size="lg" />
                                      </div>
                                      <h5 className="mb-0 fw-bold">Improvement Suggestions</h5>
                                    </div>
                                    <div className="p-4 bg-white">
                                      {typeof analysis.analysis_json.suggestions === 'string' ? (
                                        <div className="suggestion-content p-3 border-start border-warning ps-4">
                                          <p className="mb-0 lh-lg">{analysis.analysis_json.suggestions}</p>
                                        </div>
                                      ) : analysis.analysis_json.suggestions && typeof analysis.analysis_json.suggestions === 'object' ? (
                                        <div className="suggestion-sections">
                                          {analysis.analysis_json.suggestions.suggestions && (
                                            <div className="suggestion-item mb-4 p-3 bg-light rounded">
                                              <h6 className="fw-bold mb-3 d-flex align-items-center">
                                                <FontAwesomeIcon icon={faClipboardList} className="me-2 text-warning" />
                                                General Suggestions
                                              </h6>
                                              <div className="ps-4 border-start border-warning">
                                                <p className="mb-0 lh-lg">{analysis.analysis_json.suggestions.suggestions}</p>
                                              </div>
                                            </div>
                                          )}
                                          {analysis.analysis_json.suggestions.key_frameworks && (
                                            <div className="suggestion-item p-3 bg-light rounded">
                                              <h6 className="fw-bold mb-3 d-flex align-items-center">
                                                <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-info" />
                                                Recommended Frameworks
                                              </h6>
                                              <div className="ps-4 border-start border-info">
                                                <p className="mb-0 lh-lg">{analysis.analysis_json.suggestions.key_frameworks}</p>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="alert alert-light border">
                                          <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-muted" />
                                          <span className="text-muted">No improvement suggestions available</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Policy Tag Matches Card - Modern Design */}
                              <div className="analysis-card bg-white rounded shadow-sm mb-4">
                                <div className="section-header p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
                                  <h4 className="mb-0 d-flex align-items-center">
                                    <div className="icon-circle bg-info text-white me-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%' }}>
                                      <FontAwesomeIcon icon={faTags} />
                                    </div>
                                    Policy Tag Matches
                                  </h4>
                                  <div className="d-flex align-items-center">
                                    <Button 
                                      variant="outline-info" 
                                      size="sm" 
                                      className="me-2 d-flex align-items-center" 
                                      onClick={handleOpenEditTagsModal}
                                    >
                                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                                      Edit Tags
                                    </Button>
                                    <Badge bg="info" className="px-3 py-2">
                                      {analysis.analysis_json.tag_matches?.length || 0} Tags
                                    </Badge>
                                  </div>
                                </div>
                                <div className="p-4">
                                  {/* Selected Tags Display */}
                                  {tags && tags.length > 0 && (
                                    <div className="selected-tags mb-4">
                                      <h6 className="fw-bold mb-3 d-flex align-items-center">
                                        <FontAwesomeIcon icon={faTag} className="me-2 text-info" />
                                        Selected Document Tags
                                      </h6>
                                      <div className="d-flex flex-wrap gap-2">
                                        {tags.map((tag, idx) => (
                                          <Badge 
                                            key={idx} 
                                            bg="secondary" 
                                            className="p-2 mb-2 d-flex align-items-center" 
                                            style={{ fontSize: '0.9rem', fontWeight: 'normal' }}
                                          >
                                            <FontAwesomeIcon icon={faTag} className="me-1" />
                                            {tag}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {analysis.analysis_json.tag_matches && analysis.analysis_json.tag_matches.length > 0 ? (
                                    <div className="tag-matches">
                                      {analysis.analysis_json.tag_matches
                                        .sort((a, b) => b.confidence - a.confidence)
                                        .map((tag, index) => (
                                          <div key={index} className="tag-match-item mb-3 border rounded shadow-sm overflow-hidden">
                                            <div className="d-flex justify-content-between align-items-center p-3 bg-white border-bottom">
                                              <h6 className="mb-0 fw-bold d-flex align-items-center">
                                                <FontAwesomeIcon icon={faTag} className="me-2 text-info" />
                                                {tag.tag}
                                              </h6>
                                              <Badge 
                                                bg={tag.confidence > 70 ? 'success' : 
                                                    tag.confidence > 40 ? 'warning' : 'danger'}
                                                className="px-3 py-2"
                                                style={{ fontSize: '0.9rem' }}
                                              >
                                                <FontAwesomeIcon icon={faPercent} className="me-1" />
                                                {tag.confidence}%
                                              </Badge>
                                            </div>
                                            <div className="p-3 bg-white">
                                              <div className="tag-explanation mb-0 lh-lg">
                                                <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-secondary" />
                                                {tag.explanation}
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      }
                                    </div>
                                  ) : (
                                    <div className="alert alert-light border text-center p-4">
                                      <FontAwesomeIcon icon={faSearch} size="2x" className="mb-3 text-muted" />
                                      <p className="mb-0 text-muted">No policy tag matches found for this document</p>
                                    </div>
                                  )}
                                  </div>
                                </div>

                            </Col>
                          </Row>
                        </>
                      ) : (
                        <div className="analysis-card bg-white rounded shadow-sm mb-4">
                          <div className="section-header p-3 border-bottom">
                            <h4 className="mb-0">
                              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                              Analysis Error
                            </h4>
                          </div>
                          <div className="p-3">
                            <Alert variant="warning">
                              <p className="mb-0">Analysis data is not available in the structured format. Please re-analyze the document.</p>
                            </Alert>
                            <div className="text-center mt-3">
                              <Button 
                                variant="primary"
                                onClick={handleAnalyzeDocument}
                                disabled={analyzing}
                              >
                                {analyzing ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                    Analyzing...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faSync} className="me-2" />
                                    Re-Analyze Document
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="analysis-card bg-white rounded shadow-sm mb-4">
                      <div className="section-header p-3 border-bottom">
                        <h4 className="mb-0">
                          <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary" />
                          Loading Analysis
                        </h4>
                      </div>
                      <div className="p-3">
                        <div className="text-center py-5">
                          <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
                          <h4>Loading analysis results...</h4>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="analysis-card bg-white rounded shadow-sm mb-4">
                    <div className="section-header p-3 border-bottom">
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faChartPie} className="me-2 text-primary" />
                        Analysis Required
                      </h4>
                    </div>
                    <div className="p-3">
                      <div className="text-center py-5">
                        <Alert variant="warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                          This document has not been analyzed yet.
                        </Alert>
                        <Button 
                          variant="primary"
                          onClick={handleAnalyzeDocument}
                          disabled={analyzing}
                          className="mt-3"
                        >
                          {analyzing ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSync} className="me-2" />
                              Analyze Document
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
       
        </Container>
      </div>
    <Modal show={showEditTagsModal} onHide={() => setShowEditTagsModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faTags} className="me-2" />
          Edit Document Tags
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="tag-editor">
          {/* Predefined Policy Tags Section */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FontAwesomeIcon icon={faTags} className="me-2" />
              Select Policy Tags
            </Form.Label>
            <Select
              isMulti
              name="policy-tags"
              options={policyTags.map(tag => ({ value: tag, label: tag }))}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Select policy tags..."
              value={editableTags.map(tag => ({ value: tag.tag, label: tag.tag }))}
              onChange={(selectedOptions) => {
                // Create new tags array with selected options
                const newTags = selectedOptions.map(option => {
                  // Check if tag already exists in editableTags
                  const existingTag = editableTags.find(t => t.tag === option.value);
                  if (existingTag) {
                    return existingTag; // Keep existing tag data
                  } else {
                    return { tag: option.value, confidence: 70, isEdited: true }; // Create new tag
                  }
                });
                
                setEditableTags(newTags);
              }}
              isDisabled={isReanalyzing}
              styles={customStyles}
            />
            {editableTags.length > 0 && (
              <div className="mt-2">
                <small className="text-muted">
                  Selected tags: {editableTags.length}
                </small>
              </div>
            )}
          </Form.Group>
          
          {/* Compliance Frameworks Section */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FontAwesomeIcon icon={faFileAlt} className="me-2" />
              Select Compliance Frameworks for Analysis
            </Form.Label>
            <Select
              isMulti
              name="compliance-frameworks"
              options={frameworks.map(framework => ({ value: framework, label: framework }))}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Select compliance frameworks..."
              value={editableFrameworks.map(fw => ({ value: fw.framework || fw.value, label: fw.framework || fw.value }))}
              onChange={(selectedOptions) => {
                // Create new frameworks array with selected options
                const newFrameworks = selectedOptions.map(option => {
                  // Check if framework already exists in editableFrameworks
                  const existingFramework = editableFrameworks.find(f => 
                    (f.framework && f.framework === option.value) || (f.value && f.value === option.value)
                  );
                  if (existingFramework) {
                    return existingFramework; // Keep existing framework data
                  } else {
                    return { framework: option.value, isEdited: true }; // Create new framework
                  }
                });
                
                setEditableFrameworks(newFrameworks);
              }}
              isDisabled={isReanalyzing}
              styles={customStyles}
            />
            {editableFrameworks.length > 0 && (
              <div className="mt-2">
                <small className="text-muted">
                  Selected frameworks: {editableFrameworks.length}
                </small>
              </div>
            )}
          </Form.Group>
          
          {/* Custom Tag Input */}
          <Form.Group className="mb-4">
            <Form.Label>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Custom Tag
            </Form.Label>
            <div className="d-flex align-items-center">
              <Form.Control
                type="text"
                placeholder="Enter custom tag"
                value={customTag}
                onChange={(e) => setCustomTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                disabled={isReanalyzing}
                className="me-2"
              />
              <Button 
                variant="outline-primary" 
                onClick={handleAddTag}
                disabled={!customTag.trim() || isReanalyzing}
                className="d-inline-flex align-items-center justify-content-center"
                style={{ minWidth: '100px' }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Tag
                </Button>
              </div>
            </Form.Group>
            
            {/* Current Tags Display */}
            <Form.Group className="mb-4">
              <Form.Label>Current Tags:</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {editableTags.length > 0 ? (
                  editableTags.map((tag, index) => (
                  <Badge 
                    key={index} 
                    bg={tag.isEdited ? "primary" : "secondary"} 
                    className="p-2 me-1 mb-1 d-flex align-items-center"
                    style={{ fontSize: '0.9rem' }}
                  >
                    {tag.tag}
                    <Button 
                      variant="link" 
                      className="p-0 ms-2 text-white" 
                      onClick={() => handleRemoveTag(index)}
                      disabled={isReanalyzing}
                      aria-label={`Remove tag ${tag.tag}`}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </Badge>
                ))
              ) : (
                <div className="text-muted">No tags selected</div>
              )}
            </div>
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowEditTagsModal(false)}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSaveTagEdits}
          disabled={isReanalyzing}
        >
          {isReanalyzing ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Saving...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              Save Changes
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Document Query with AI Slider */}
    {showQuerySlider && (
      <div 
        className="slider-overlay visible" 
        onClick={toggleQuerySlider}
      />
    )}
    <div className={`query-slider ${showQuerySlider ? 'open' : ''}`}>
      <div className="slider-header p-3 border-bottom d-flex justify-content-between align-items-center bg-primary text-white">
        <h4 className="mb-0">
          <FontAwesomeIcon icon={faLightbulb} className="me-2" />
          Chat with Document
        </h4>
        <Button 
          variant="link" 
          className="text-white p-0" 
          onClick={toggleQuerySlider}
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </Button>
      </div>
      {/* Chat Messages Area */}
      <div ref={chatMessagesRef} className="chat-messages-container flex-grow-1 overflow-auto p-3">
        {/* Welcome message */}
        <div className="mb-3 border-bottom pb-3">
          <div className="p-3 bg-light rounded">
            <h6 className="mb-1">Document Q&A</h6>
            <p className="mb-0 small text-muted">Ask questions about compliance requirements, key points, or specific sections of this document.</p>
          </div>
        </div>
        
        {/* Loading indicator for chat history */}
        {loadingChatHistory && (
          <div className="text-center my-3">
            <FontAwesomeIcon icon={faSpinner} spin />
            <p className="small text-muted mt-2">Loading chat history...</p>
          </div>
        )}
        
        {/* Chat History Messages */}
        {chatHistory.length > 0 && chatHistory.map((chat, index) => (
          <React.Fragment key={chat.id || index}>
            {/* User Query Message */}
            <div className="mb-3">
              <div className="d-flex align-items-center mb-1">
                <strong className="me-2">Question:</strong>
                <span className="text-muted small">
                  {new Date(chat.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>
              <div className="p-3 bg-light border rounded">
                <p className="mb-0">{chat.query}</p>
              </div>
            </div>
            
            {/* Response Message */}
            <div className="mb-4 ms-3">
              <div className="d-flex align-items-center mb-1">
                <strong className="me-2">Answer:</strong>
                <span className="text-muted small">
                  {new Date(chat.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>
              <div className="p-3 bg-white border rounded">
                {typeof chat.response.answer === 'string' ? (
                  <div className="mb-3">
                    <p style={{ whiteSpace: 'pre-line' }}>{chat.response.answer}</p>
                  </div>
                ) : null}
                
                {chat.response.references && chat.response.references.length > 0 && (
                  <div className="mb-3">
                    <div className="fw-bold mb-2 small">References:</div>
                    <ul className="mb-0 small">
                      {chat.response.references.map((ref, idx) => (
                        <li key={idx}>{ref}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {chat.response.compliance_context && (
                  <div className="mb-0">
                    <div className="fw-bold mb-2 small">Compliance Context:</div>
                    <p className="mb-0 small">{chat.response.compliance_context}</p>
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
        
        {/* Current User Query */}
        {currentUserQuery && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-1">
              <strong className="me-2">Question:</strong>
              <span className="text-muted small">
                {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </span>
            </div>
            <div className="p-3 bg-light border rounded">
              <p className="mb-0">{currentUserQuery}</p>
            </div>
          </div>
        )}
        
        {/* Response with Typing Effect or Loading */}
        {(isQuerying || isTyping || (queryResponse && currentUserQuery)) && !chatHistory.some(chat => 
          (queryResponse && chat.query === currentUserQuery && 
          JSON.stringify(chat.response) === JSON.stringify(queryResponse))
        ) && (
          <div className="mb-4 ms-3">
            <div className="d-flex align-items-center mb-1">
              <strong className="me-2">Answer:</strong>
              {!isQuerying && !isTyping && queryResponse && (
                <span className="text-muted small">
                  {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              )}
            </div>
            <div className="p-3 bg-white border rounded">
              {isQuerying ? (
                <div className="text-center py-3">
                  <FontAwesomeIcon icon={faSpinner} spin size="lg" />
                  <p className="mt-2 mb-0 small text-muted">Processing query...</p>
                </div>
              ) : isTyping && displayedResponse ? (
                <div className="mb-3">
                  <p style={{ whiteSpace: 'pre-line' }}>{displayedResponse}<span className="typing-cursor">|</span></p>
                </div>
              ) : queryResponse && queryResponse.answer ? (
                <>
                  <div className="mb-3">
                    <p style={{ whiteSpace: 'pre-line' }}>{queryResponse.answer}</p>
                  </div>
                  
                  {queryResponse.references && queryResponse.references.length > 0 && (
                    <div className="mb-3">
                      <div className="fw-bold mb-2 small">References:</div>
                      <ul className="mb-0 small">
                        {queryResponse.references.map((ref, idx) => (
                          <li key={idx}>{ref}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {queryResponse.compliance_context && (
                    <div className="mb-0">
                      <div className="fw-bold mb-2 small">Compliance Context:</div>
                      <p className="mb-0 small">{queryResponse.compliance_context}</p>
                    </div>
                  )}
                </>
              ) : error ? (
                <div className="text-center py-3">
                  <p className="mb-0 text-danger">{error}</p>
                </div>
              ) : (
                <div className="text-center py-3">
                  <p className="mb-0 text-muted">No response received. Please try again.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Fixed Chat Input Area */}
      <div className="chat-input-container p-3 border-top">
        <Form onSubmit={handleDocumentQuery}>
          <div className="d-flex">
            <Form.Control
              type="text"
              placeholder="Ask a question about this document..."
              value={documentQuery}
              onChange={(e) => setDocumentQuery(e.target.value)}
              disabled={isQuerying}
              className="me-2"
            />
            <Button 
              type="submit" 
              variant="primary" 
              disabled={isQuerying || !documentQuery.trim()}
            >
              {isQuerying ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
    </>
  );
};

export default DocumentDetail;
